/**
 * BrainSwipe API Client
 */
import type { SwipeQuestion, SwipeSession, AnswerResult, CategoryInfo } from '../types/swipe';

const API_BASE = '/api/swipe';

// ============================================================================
// TYPES
// ============================================================================

export interface TagInfo {
  name: string;
  questionCount: number;
}

// ============================================================================
// AUTH
// ============================================================================

import { getAuthHeaders } from "./auth-headers";

function saveGuestId(guestId: string): void {
  localStorage.setItem('beshek_guest_id', guestId);
}

// ============================================================================
// API CALLS
// ============================================================================

interface NextQuestionsResponse {
  questions: SwipeQuestion[];
  session: SwipeSession;
  guestId?: string;
}

export async function fetchNextQuestions(options: {
  count?: number;
  category?: string;
  difficulty?: string;
  excludeIds?: string[];
  quizIds?: string[];  // Фильтр по ID викторин
  tag?: string;        // Фильтр по тегу
} = {}): Promise<NextQuestionsResponse> {
  const params = new URLSearchParams();

  if (options.count) params.set('count', String(options.count));
  if (options.category) params.set('category', options.category);
  if (options.difficulty) params.set('difficulty', options.difficulty);
  if (options.excludeIds?.length) params.set('excludeIds', options.excludeIds.join(','));
  if (options.quizIds?.length) params.set('quiz', options.quizIds.join(','));
  if (options.tag) params.set('tag', options.tag);

  const url = `${API_BASE}/next?${params.toString()}`;

  const response = await fetch(url, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch questions: ${response.status}`);
  }

  const data = await response.json();

  // Save guest ID if returned
  if (data.guestId) {
    saveGuestId(data.guestId);
  }

  return data;
}

export async function submitAnswer(
  questionId: string,
  answerIndex: number,
  timeSpent: number
): Promise<AnswerResult> {
  const response = await fetch(`${API_BASE}/answer`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ questionId, answerIndex, timeSpent }),
  });

  if (!response.ok) {
    throw new Error(`Failed to submit answer: ${response.status}`);
  }

  return response.json();
}

export async function fetchCategories(): Promise<CategoryInfo[]> {
  const response = await fetch(`${API_BASE}/categories`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch categories: ${response.status}`);
  }

  const data = await response.json();
  return data.categories;
}

/**
 * Получить список тегов с количеством вопросов
 */
export async function fetchTags(): Promise<TagInfo[]> {
  const response = await fetch(`${API_BASE}/tags`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch tags: ${response.status}`);
  }

  const data = await response.json();
  return data.tags;
}

export async function fetchSessionStats(): Promise<SwipeSession & {
  duration: number;
  maxStreak: number;
  accuracy: number;
}> {
  const response = await fetch(`${API_BASE}/stats`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch stats: ${response.status}`);
  }

  return response.json();
}

export async function resetSession(): Promise<void> {
  const response = await fetch(`${API_BASE}/reset`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to reset session: ${response.status}`);
  }
}